import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  colorSchemes,
  generateCSSVariables,
  DEFAULT_THEME,
  DEFAULT_MODE,
  getSystemPreference,
  THEMES,
  MODES
} from './colorSchemes';

const ThemeContext = createContext({});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize from HTML classes set by pre-paint script
  const getInitialMode = () => {
    if (typeof window === 'undefined') return DEFAULT_MODE;
    const root = document.documentElement;
    if (root.classList.contains('dark')) return MODES.DARK;
    if (root.classList.contains('light')) return MODES.LIGHT;
    return DEFAULT_MODE;
  };

  const [currentTheme, setCurrentTheme] = useState(DEFAULT_THEME);
  const [currentMode, setCurrentMode] = useState(getInitialMode());
  const [isSystemMode, setIsSystemMode] = useState(false);

  // Load saved theme from localStorage on mount (mode already set by pre-paint script)
  useEffect(() => {
    const savedTheme = localStorage.getItem('ev-theme');
    const savedSystemMode = localStorage.getItem('ev-system-mode') === 'true';

    if (savedTheme && Object.values(THEMES).includes(savedTheme)) {
      setCurrentTheme(savedTheme);
    }

    if (savedSystemMode) {
      setIsSystemMode(true);
    }

    // Remove no-transition class after first paint
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('theme-no-transition');
    });
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    if (!isSystemMode) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setCurrentMode(e.matches ? MODES.DARK : MODES.LIGHT);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isSystemMode]);

  // Apply CSS variables when theme/mode changes
  useEffect(() => {
    const cssVariables = generateCSSVariables(currentTheme, currentMode);
    const root = document.documentElement;

    Object.entries(cssVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Update theme classes - preserve pre-paint classes, just update mode
    root.classList.remove('light', 'dark');
    root.classList.add(currentMode);
    root.setAttribute('data-theme', currentMode);
    root.style.colorScheme = currentMode;

    // Add theme class for additional styling
    root.classList.add(`theme-${currentTheme}`, `mode-${currentMode}`);
  }, [currentTheme, currentMode]);

  // Save preferences to localStorage using single source of truth
  useEffect(() => {
    localStorage.setItem('ck-theme', currentMode); // Single storage key
    localStorage.setItem('ev-theme', currentTheme); // Theme variant (ocean-blue, etc)
    localStorage.setItem('ev-system-mode', isSystemMode.toString());
  }, [currentTheme, currentMode, isSystemMode]);

  const changeTheme = (theme) => {
    if (Object.values(THEMES).includes(theme)) {
      setCurrentTheme(theme);
    }
  };

  const changeMode = (mode) => {
    if (Object.values(MODES).includes(mode)) {
      // Disable transitions during theme change
      document.documentElement.classList.add('theme-transitioning');
      setIsSystemMode(false);
      setCurrentMode(mode);
      // Wait for 2 animation frames to ensure DOM repaint completes
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.documentElement.classList.remove('theme-transitioning');
        });
      });
    }
  };

  const toggleMode = () => {
    // Disable transitions during theme toggle
    document.documentElement.classList.add('theme-transitioning');
    setIsSystemMode(false);
    setCurrentMode(currentMode === MODES.LIGHT ? MODES.DARK : MODES.LIGHT);
    // Wait for 2 animation frames to ensure DOM repaint completes
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.documentElement.classList.remove('theme-transitioning');
      });
    });
  };

  const enableSystemMode = () => {
    // Disable transitions during theme change
    document.documentElement.classList.add('theme-transitioning');
    setIsSystemMode(true);
    setCurrentMode(getSystemPreference());
    // Wait for 2 animation frames to ensure DOM repaint completes
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.documentElement.classList.remove('theme-transitioning');
      });
    });
  };

  const getCurrentColors = () => {
    return colorSchemes[currentTheme][currentMode];
  };

  const getCurrentThemeInfo = () => {
    return colorSchemes[currentTheme];
  };

  const themeContextValue = {
    currentTheme,
    currentMode,
    isSystemMode,
    changeTheme,
    changeMode,
    toggleMode,
    enableSystemMode,
    getCurrentColors,
    getCurrentThemeInfo,
    themes: THEMES,
    modes: MODES,
    colorSchemes
  };

  return (
    <ThemeContext.Provider value={themeContextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Higher-order component for theme-aware components
export const withTheme = (Component) => {
  return (props) => {
    const themeProps = useTheme();
    return <Component {...props} {...themeProps} />;
  };
};

// Video player wrapper with theme-aware styling
export const VideoPlayerWrapper = ({ children, className = '', ...props }) => {
  const { getCurrentColors } = useTheme();
  const colors = getCurrentColors();

  const videoStyle = {
    '--video-overlay': colors.videoOverlay,
    '--video-controls': colors.videoControls,
    '--video-progress': colors.videoProgress,
  };

  return (
    <div
      className={`video-player-wrapper ${className}`}
      style={videoStyle}
      {...props}
    >
      {children}
    </div>
  );
};